/** @typedef {(method: string, url: string, headers?: Headers|Record<string, string>, body?: unknown, includeCredentials?: boolean) => unknown} MakeRequest */

/** @type {WeakSet<{ makeRequest: MakeRequest }>} */
let instrumentedClients = new WeakSet();
/** @type {WeakSet<{ makeRequest: MakeRequest }>} */
let instrumentedPrototypes = new WeakSet();
let fetchInstrumented = false;
const sessionStorageKey = 'homekeepr.observability.session-id';
/** @type {string|null} */
let memorySessionId = null;

/**
 * @returns {string}
 */
export function createRequestId() {
	const cryptoObject = globalThis.crypto;

	if (cryptoObject && typeof cryptoObject.randomUUID === 'function') {
		return cryptoObject.randomUUID();
	}

	return [Date.now().toString(16), Math.random().toString(16).slice(2)].join(
		'-'
	);
}

/**
 * @returns {string}
 */
export function getSessionId() {
	if (memorySessionId) {
		return memorySessionId;
	}

	try {
		const existingSessionId =
			window.sessionStorage.getItem(sessionStorageKey);

		if (existingSessionId) {
			memorySessionId = existingSessionId;
			return memorySessionId;
		}

		memorySessionId = createRequestId();
		window.sessionStorage.setItem(sessionStorageKey, memorySessionId);

		return memorySessionId;
	} catch (_error) {
		memorySessionId = createRequestId();
		return memorySessionId;
	}
}

/**
 * @param {Headers|Record<string, string>|Array<[string, string]>|undefined} headers
 * @returns {Headers}
 */
export function createTraceHeaders(headers) {
	const tracedHeaders =
		headers instanceof Headers
			? new Headers(headers)
			: new Headers(headers || {});
	const traceId = tracedHeaders.get('x-trace-id') || createRequestId();
	const requestId = tracedHeaders.get('x-request-id') || traceId;
	const sessionId = tracedHeaders.get('x-session-id') || getSessionId();

	tracedHeaders.set('x-request-id', requestId);
	tracedHeaders.set('x-session-id', sessionId);
	tracedHeaders.set('x-trace-id', traceId);

	return tracedHeaders;
}

/**
 * @param {string} url
 * @returns {boolean}
 */
export function shouldTraceRequest(url) {
	try {
		const resolvedUrl = new URL(url, window.location.origin);

		return (
			resolvedUrl.origin === window.location.origin &&
			resolvedUrl.pathname.startsWith('/api/')
		);
	} catch (_error) {
		return false;
	}
}

/**
 * @param {unknown} input
 * @returns {string|null}
 */
function getTraceableFetchUrl(input) {
	if (typeof input === 'string' || input instanceof URL) {
		return String(input);
	}

	if (typeof Request !== 'undefined' && input instanceof Request) {
		return input.url;
	}

	return null;
}

/**
 * @param {Request|undefined} request
 * @param {HeadersInit|undefined} overrideHeaders
 * @returns {Headers}
 */
function createFetchTraceHeaders(request, overrideHeaders) {
	const mergedHeaders = new Headers(request?.headers || undefined);

	if (overrideHeaders) {
		new Headers(overrideHeaders).forEach((value, key) => {
			mergedHeaders.set(key, value);
		});
	}

	return createTraceHeaders(mergedHeaders);
}

/**
 * @returns {void}
 */
export function instrumentGlobalFetch() {
	if (fetchInstrumented || typeof globalThis.fetch !== 'function') {
		return;
	}

	const originalFetch =
		/** @type {(input: unknown, init?: RequestInit) => Promise<Response>} */ (
			globalThis.fetch.bind(globalThis)
		);

	globalThis.fetch = function tracedFetch(input, init) {
		const traceableUrl = getTraceableFetchUrl(input);

		if (!traceableUrl || !shouldTraceRequest(traceableUrl)) {
			return originalFetch(input, init);
		}

		if (typeof Request !== 'undefined' && input instanceof Request) {
			return originalFetch(
				new Request(input, {
					...init,
					headers: createFetchTraceHeaders(input, init?.headers),
				})
			);
		}

		return originalFetch(input, {
			...init,
			headers: createTraceHeaders(init?.headers),
		});
	};

	fetchInstrumented = true;
}

/**
 * @template {{ makeRequest: MakeRequest }} HttpClient
 * @param {HttpClient} httpClient
 * @returns {HttpClient}
 */
export function instrumentHttpClient(httpClient) {
	if (instrumentedClients.has(httpClient)) {
		return httpClient;
	}

	const originalMakeRequest = httpClient.makeRequest.bind(httpClient);

	httpClient.makeRequest = function makeTracedRequest(
		/** @type {string} */ method,
		/** @type {string} */ url,
		/** @type {Headers|Record<string, string>|undefined} */ headers,
		/** @type {unknown} */ body,
		/** @type {boolean|undefined} */ includeCredentials
	) {
		const tracedHeaders = shouldTraceRequest(url)
			? createTraceHeaders(headers)
			: headers;

		return originalMakeRequest(
			method,
			url,
			tracedHeaders,
			body,
			includeCredentials
		);
	};

	instrumentedClients.add(httpClient);

	return httpClient;
}

/**
 * @param {{ makeRequest: MakeRequest }} httpClientPrototype
 * @returns {void}
 */
export function instrumentHttpClientPrototype(httpClientPrototype) {
	if (instrumentedPrototypes.has(httpClientPrototype)) {
		return;
	}

	const originalMakeRequest = httpClientPrototype.makeRequest;

	httpClientPrototype.makeRequest = function makeTracedRequest(
		/** @type {string} */ method,
		/** @type {string} */ url,
		/** @type {Headers|Record<string, string>|undefined} */ headers,
		/** @type {unknown} */ body,
		/** @type {boolean|undefined} */ includeCredentials
	) {
		const tracedHeaders = shouldTraceRequest(url)
			? createTraceHeaders(headers)
			: headers;

		return originalMakeRequest.call(
			this,
			method,
			url,
			tracedHeaders,
			body,
			includeCredentials
		);
	};

	instrumentedPrototypes.add(httpClientPrototype);
}

/**
 * @returns {void}
 */
export function resetRequestTracingForTests() {
	instrumentedClients = new WeakSet();
	instrumentedPrototypes = new WeakSet();
	fetchInstrumented = false;
	memorySessionId = null;
}
